import { STANDARD_ABILITY_CERTIFICATIONS_RENOVATION_PERIOD_UNITS } from "frontend/constants/standards"

const DAYS_IN_A_MONTH = 31
const DAYS_IN_A_YEAR = 365

export type StandardAbilityCertificationAPIPayload = {
  id?: number,
  acronym: string,
  description: string,
  renovation_period_units: number | null,
  renovation_period_quantity: number | null,
  deletable?: boolean,
  notification_threshold_quantity: number | null,
  notification_threshold_units: number | null,
  enabled: boolean,
  allowed_certifiers: number[],
}

export class StandardAbilityCertification {
  id?: number
  acronym: string
  description: string
  expirationTimeUnits: number | null
  expirationTimeQuantity: number | null
  expirationNotificationThresholdQuantity: number | null
  expirationNotificationThresholdUnits: number | null
  enabled: boolean
  deletable?: boolean
  allowedCertifiers: number[]

  constructor (payload: StandardAbilityCertificationAPIPayload = {
    acronym: '',
    description: '',
    renovation_period_units: null,
    renovation_period_quantity: null,
    notification_threshold_quantity: null,
    notification_threshold_units: null,
    enabled: false,
    allowed_certifiers: [],
  }) {
    this.id = payload.id
    this.acronym = payload.acronym
    this.description = payload.description
    this.expirationTimeUnits = payload.renovation_period_units
    this.expirationTimeQuantity = payload.renovation_period_quantity
    this.expirationNotificationThresholdQuantity = payload.notification_threshold_quantity
    this.expirationNotificationThresholdUnits = payload.notification_threshold_units
    this.enabled = payload.enabled
    this.deletable = payload.deletable
    this.allowedCertifiers = payload.allowed_certifiers
  }

  get updateApiPayload () {
    return this._toApiPayload(true)
  }

  get createApiPayload () {
    return this._toApiPayload()
  }

  get expirationTimeInDays () {
    return this._timeSpanInDays(this.expirationTimeQuantity, this.expirationTimeUnits)
  }

  get expirationNotificationThresholdInDays () {
    return this._timeSpanInDays(this.expirationNotificationThresholdQuantity, this.expirationNotificationThresholdUnits)
  }

  get compoundName () {
    return `${this.acronym}: ${this.description}`
  }

  _timeSpanInDays (quantity: number | null, units: number | null) {
    if (quantity == null) {
      return NaN
    }

    if (units === STANDARD_ABILITY_CERTIFICATIONS_RENOVATION_PERIOD_UNITS.days.value) {
      return quantity
    }

    if (units === STANDARD_ABILITY_CERTIFICATIONS_RENOVATION_PERIOD_UNITS.months.value) {
      return quantity * DAYS_IN_A_MONTH
    }

    if (units === STANDARD_ABILITY_CERTIFICATIONS_RENOVATION_PERIOD_UNITS.years.value) {
      return quantity * DAYS_IN_A_YEAR
    }
  }

  _toApiPayload (update = false) {
    const apiPayload: StandardAbilityCertificationAPIPayload = {
      acronym: this.acronym,
      description: this.description,
      enabled: this.enabled,
      renovation_period_units: null,
      renovation_period_quantity: null,
      notification_threshold_quantity: null,
      notification_threshold_units: null,
      allowed_certifiers: [],
    }

    if (this.expirationTimeQuantity) {
      apiPayload['renovation_period_units'] = this.expirationTimeUnits
      apiPayload['renovation_period_quantity'] = this.expirationTimeQuantity
    } else if (update){
      apiPayload['renovation_period_units'] = null
      apiPayload['renovation_period_quantity'] = null
    }

    if (this.expirationNotificationThresholdQuantity) {
      apiPayload['notification_threshold_quantity'] = this.expirationNotificationThresholdQuantity
      apiPayload['notification_threshold_units'] = this.expirationNotificationThresholdUnits
    } else if(update){
      apiPayload['notification_threshold_quantity'] = null
      apiPayload['notification_threshold_units'] = null
    }

    if (this.allowedCertifiers) {
      apiPayload['allowed_certifiers'] = this.allowedCertifiers
    }

    return apiPayload
  }
}
